/**
 * RxFormSafety Web Component
 * Migrated from src/alpine/components/rx-form--safety.js
 *
 * Custom element <rx-form-safety> manages the prescription form:
 * - File upload + OCR analysis
 * - Manual RX entry (SPH, CYL, AXIS, ADD, PD, PRISM)
 * - Value picker drawer
 * - PD iframe integration
 * - Form submission → cart
 *
 * Receives parent context via setContext() called by the parent
 * Alpine component (hero-safety-frame-and-lenses) during Phase 2→3 transition.
 */
(function () {
  "use strict";

  var MO = window.MO;

  var SELECTORS = {
    PARENT_WRAPPER: ".js-values-container-wrapper",
    DRAWER_HEADING: ".js-drawer-heading",
    DRAWER_OPTIONS_VALUES_HEADING: ".js-options-values-heading",
    PARENT: ".js-values-container",
    DEFAULT_VALUE_BTN: ".js-default-value-btn",
    DYNAMIC_VALUES: ".js-dynamic-values-container",
  };

  // ─── Default form field state ────────────────────────────────────
  function defaultManualFormField() {
    return {
      SPH: { R: "0.00", L: "0.00" },
      CYL: { R: "0.00", L: "0.00" },
      AXIS: { R: "000", L: "000" },
      PUPIL_RL: { RL: "None" },
      PUPIL: { R: "None", L: "None" },
      ADD: { R: "N/A", L: "N/A" },
      PRISM: {
        R: {
          vertical: { direction: "U", value: "0.00" },
          horizontal: { direction: "I", value: "0.00" },
        },
        L: {
          vertical: { direction: "U", value: "0.00" },
          horizontal: { direction: "I", value: "0.00" },
        },
      },
      COMMENT: "",
    };
  }

  // ─── Web Component ───────────────────────────────────────────────
  class RxFormSafety extends HTMLElement {
    constructor() {
      super();

      this.state = {
        step: "INITIAL",
        previewUrl: "",
        uniqueId: null,
        rxSource: null,
        isUploading: false,
        uploaded: false,
        file: null,
        isOpenDrawer: false,
        isShowPlusMinusHeading: true,
        manualFormFieldKey: "",
        drawerOptionTitle: "",
        drawerEye: "",
        drawerValues: {
          defaultValue: "",
          positiveValues: [],
          negativeValues: [],
        },
        isOpenPD: false,
        isOpenPrism: false,
        manualFormField: defaultManualFormField(),
        pdImage: null,
        maskSize: null,
        requiredFields: [],
        isWaitAnalyze: false,
        isYearIssued: false,
        isYearExpired: false,
        waitingAnalyzeIndex: 0,
        waitingAnalyzeText: "",
        isShowScrollDownBtn: true,
        isShowScrollUpBtn: false,
      };

      // Parent context — set via setContext()
      this._ctx = {
        rxLensSelectedVariant: null,
        selectedVariant: null,
        rxLensProduct: null,
        frameTag: "",
        frameName: "",
        isAddedCoatingProduct: false,
        lensCoatingSelectedVariant: null,
        customSize: "",
        temple1: null,
        temple2: null,
        measurementId: null,
        activeEyewear: "",
        currencySymbol: "$",
        sectionId: "",
      };

      this._activeTypeOption = null;
      this._pdMessageHandler = null;
      this._templeMessageHandler = null;
      this._loadingInterval = null;
      this._scrollRAF = null;
      this._scannerRaf = null;
      this._scannerParticles = [];
    }

    // ── Lifecycle ────────────────────────────────────────────────
    connectedCallback() {
      var sid = this.getAttribute("data-section-id");
      if (sid) this._ctx.sectionId = sid;
      var cs = this.getAttribute("data-currency-symbol");
      if (cs) this._ctx.currencySymbol = cs;

      this._bindEvents();
      this._initMessageListeners();
      this._initMaskSizeListener();
      this._initScrollObserver();
      this.render();
    }

    disconnectedCallback() {
      this.destroyScannerAnimation();
      if (this._pdMessageHandler)
        window.removeEventListener("message", this._pdMessageHandler);
      if (this._templeMessageHandler)
        window.removeEventListener("message", this._templeMessageHandler);
      if (this._loadingInterval) clearInterval(this._loadingInterval);
    }

    // ── Public API (called by parent) ────────────────────────────
    setContext(ctx) {
      Object.assign(this._ctx, ctx);
      this._updateActiveTypeOption();
      this.render();
    }

    // ── State management ─────────────────────────────────────────
    setState(updates) {
      Object.assign(this.state, updates);
      this.render();
    }

    _setFieldValue(path, value) {
      var parts = path.split(".");
      var obj = this.state.manualFormField;
      for (var i = 0; i < parts.length - 1; i++) {
        obj = obj[parts[i]];
      }
      var lastKey = parts[parts.length - 1];
      obj[lastKey] = value;
      this._onFieldChange(path, value);
      this.render();
    }

    _getFieldValue(path) {
      var parts = path.split(".");
      var obj = this.state.manualFormField;
      for (var i = 0; i < parts.length; i++) {
        obj = obj[parts[i]];
      }
      return obj;
    }

    // ── Active type option (derived from parent context) ─────────
    _updateActiveTypeOption() {
      var ctx = this._ctx;
      if (!ctx.rxLensProduct || !ctx.rxLensSelectedVariant) return;

      var types = ctx.rxLensProduct.productOptions
        ? ctx.rxLensProduct.productOptions.find(function (opt) {
            return opt.name === "Type";
          })
        : null;

      if (types) {
        var old = this._activeTypeOption;
        this._activeTypeOption = types.values.find(function (value) {
          return ctx.rxLensSelectedVariant.options.includes(value);
        });
        if (old !== this._activeTypeOption) {
          this._onActiveTypeOptionChange();
        }
      }
    }

    // ── Field change handlers (replace Alpine $watch) ────────────
    _onFieldChange(path, value) {
      var s = this.state;
      var mf = s.manualFormField;
      var req = s.requiredFields;

      // CYL.R → AXIS.R required
      if (path === "CYL.R") {
        var cylR = mf.CYL.R;
        if (cylR !== "0" && cylR !== "0.0" && cylR !== "0.00") {
          if (!req.includes("AXIS.R") && mf.AXIS.R === "000") {
            req.push("AXIS.R");
          }
        } else {
          s.requiredFields = req.filter(function (el) {
            return el !== "AXIS.R";
          });
        }
      }
      // CYL.L → AXIS.L required
      if (path === "CYL.L") {
        var cylL = mf.CYL.L;
        if (cylL !== "0" && cylL !== "0.0" && cylL !== "0.00") {
          if (!req.includes("AXIS.L") && mf.AXIS.L === "000") {
            req.push("AXIS.L");
          }
        } else {
          s.requiredFields = req.filter(function (el) {
            return el !== "AXIS.L";
          });
        }
      }
      // AXIS clears its own required
      if (path === "AXIS.R" && mf.AXIS.R !== "000") {
        s.requiredFields = req.filter(function (el) {
          return el !== "AXIS.R";
        });
      }
      if (path === "AXIS.L" && mf.AXIS.L !== "000") {
        s.requiredFields = req.filter(function (el) {
          return el !== "AXIS.L";
        });
      }
      // PD combined
      if (path === "PUPIL_RL.RL") {
        if (mf.PUPIL_RL.RL === "None" && !s.isOpenPD) {
          if (!req.includes("PUPIL_RL.RL")) req.push("PUPIL_RL.RL");
        } else {
          s.requiredFields = req.filter(function (el) {
            return el !== "PUPIL_RL.RL";
          });
        }
      }
      // PD individual
      if (path === "PUPIL.R") {
        if (mf.PUPIL.R === "None" && s.isOpenPD) {
          if (!req.includes("PUPIL.R")) req.push("PUPIL.R");
        } else {
          s.requiredFields = req.filter(function (el) {
            return el !== "PUPIL.R";
          });
        }
      }
      if (path === "PUPIL.L") {
        if (mf.PUPIL.L === "None" && s.isOpenPD) {
          if (!req.includes("PUPIL.L")) req.push("PUPIL.L");
        } else {
          s.requiredFields = req.filter(function (el) {
            return el !== "PUPIL.L";
          });
        }
      }
      // ADD
      if (path === "ADD.R" || path === "ADD.L") {
        var eye = path.split(".")[1];
        var isMultifocal =
          this._activeTypeOption === "Bifocal" ||
          this._activeTypeOption === "Progressive" ||
          this._activeTypeOption === "Lined Bifocal";
        var fieldName = "ADD." + eye;
        var isReq = req.includes(fieldName);
        var shouldBeReq = mf.ADD[eye] === "N/A" && isMultifocal;
        if (shouldBeReq && !isReq) req.push(fieldName);
        else if (!shouldBeReq && isReq)
          s.requiredFields = req.filter(function (el) {
            return el !== fieldName;
          });
      }
    }

    _onActiveTypeOptionChange() {
      var isMultifocal =
        this._activeTypeOption === "Bifocal" ||
        this._activeTypeOption === "Progressive" ||
        this._activeTypeOption === "Lined Bifocal";
      var mf = this.state.manualFormField;
      var req = this.state.requiredFields;

      ["L", "R"].forEach(
        function (eye) {
          var field = "ADD." + eye;
          var isFieldRequired = isMultifocal && mf.ADD[eye] === "N/A";
          var idx = req.indexOf(field);
          if (isFieldRequired && idx === -1) req.push(field);
          else if (!isFieldRequired && idx > -1) req.splice(idx, 1);
        }.bind(this)
      );
    }

    // ── Render ───────────────────────────────────────────────────
    render() {
      var s = this.state;
      var mf = s.manualFormField;
      var ctx = this._ctx;

      // Visibility map
      var vis = {
        stepInitial: s.step === "INITIAL",
        stepManual: s.step === "MANUAL",
        previewUrl: !!s.previewUrl,
        isHandwritten: s.rxSource === "handwritten",
        isNotHandwritten: s.rxSource !== "handwritten",
        isOpenPD: s.isOpenPD,
        notOpenPD: !s.isOpenPD,
        isOpenPrism: s.isOpenPrism,
        isWaitAnalyze: s.isWaitAnalyze,
        isShowScrollDownBtn:
          s.isShowScrollDownBtn && s.step === "MANUAL",
        isShowScrollUpBtn:
          s.isShowScrollUpBtn && s.step === "MANUAL",
        yearWarning: s.isYearIssued || s.isYearExpired,
        showAddRow:
          ctx.rxLensSelectedVariant &&
          !ctx.rxLensSelectedVariant.options.includes("Single Vision"),
        pdWarning: s.isOpenPD
          ? mf.PUPIL.R === "None" || mf.PUPIL.L === "None"
          : mf.PUPIL_RL.RL === "None",
        pdCheckmark: s.isOpenPD,
        prismCheckmark: s.isOpenPrism,
        isOpenDrawer: s.isOpenDrawer,
        isSpHCylLayout: ["SPH.R", "SPH.L", "CYL.R", "CYL.L"].includes(
          s.manualFormFieldKey
        ),
        isOtherLayout: !["SPH.R", "SPH.L", "CYL.R", "CYL.L"].includes(
          s.manualFormFieldKey
        ),
        isPlusMinusHeading:
          s.isShowPlusMinusHeading &&
          !["SPH.R", "SPH.L", "CYL.R", "CYL.L"].includes(
            s.manualFormFieldKey
          ),
      };

      this.querySelectorAll("[data-show]").forEach(function (el) {
        var key = el.getAttribute("data-show");
        if (key in vis) {
          el.style.display = vis[key] ? "" : "none";
        }
      });

      // Text content
      this._text("sphR", mf.SPH.R);
      this._text("sphL", mf.SPH.L);
      this._text("cylR", mf.CYL.R);
      this._text("cylL", mf.CYL.L);
      this._text("axisR", mf.AXIS.R);
      this._text("axisL", mf.AXIS.L);
      this._text("addR", mf.ADD.R);
      this._text("addL", mf.ADD.L);
      this._text("pdRL", mf.PUPIL_RL.RL);
      this._text("pdR", mf.PUPIL.R);
      this._text("pdL", mf.PUPIL.L);
      this._text("prismRVertVal", mf.PRISM.R.vertical.value);
      this._text("prismRHorizVal", mf.PRISM.R.horizontal.value);
      this._text("prismLVertVal", mf.PRISM.L.vertical.value);
      this._text("prismLHorizVal", mf.PRISM.L.horizontal.value);
      this._text("drawerTitle", s.drawerOptionTitle);
      this._text("drawerEye", s.drawerEye);
      this._text("waitingAnalyzeText", s.waitingAnalyzeText);

      // File info
      if (s.file) {
        var fileInfoEl = this._ref("fileInfo");
        if (fileInfoEl) {
          fileInfoEl.textContent =
            s.file.name +
            " (" +
            (s.file.size / 1024 / 1024).toFixed(2) +
            "mb)";
        }
      }

      // Preview image
      var previewImg = this._ref("previewImg");
      if (previewImg && s.previewUrl) {
        previewImg.setAttribute("src", s.previewUrl);
      }

      // Select elements (PRISM directions)
      this._syncSelect("prismRVertDir", mf.PRISM.R.vertical.direction);
      this._syncSelect("prismRHorizDir", mf.PRISM.R.horizontal.direction);
      this._syncSelect("prismLVertDir", mf.PRISM.L.vertical.direction);
      this._syncSelect("prismLHorizDir", mf.PRISM.L.horizontal.direction);

      // Error classes
      this._toggleError(
        "axisR",
        mf.CYL.R !== "0.00" &&
          mf.CYL.R !== "0" &&
          mf.AXIS.R === "000"
      );
      this._toggleError(
        "axisL",
        mf.CYL.L !== "0.00" &&
          mf.CYL.L !== "0" &&
          mf.AXIS.L === "000"
      );
      this._toggleError(
        "addR",
        (this._activeTypeOption === "Bifocal" ||
          this._activeTypeOption === "Progressive" ||
          this._activeTypeOption === "Lined Bifocal") &&
          mf.ADD.R === "N/A"
      );
      this._toggleError(
        "addL",
        (this._activeTypeOption === "Bifocal" ||
          this._activeTypeOption === "Progressive" ||
          this._activeTypeOption === "Lined Bifocal") &&
          mf.ADD.L === "N/A"
      );
      this._toggleError("pdRL", mf.PUPIL_RL.RL === "None");
      this._toggleError("pdR", mf.PUPIL.R === "None");
      this._toggleError("pdL", mf.PUPIL.L === "None");

      // Drawer overlay transition
      var overlay = this._ref("drawerOverlay");
      if (overlay) {
        if (s.isOpenDrawer) {
          overlay.classList.add("active");
        } else {
          overlay.classList.remove("active");
        }
      }
      var drawerPanel = this._ref("drawerPanel");
      if (drawerPanel) {
        if (s.isOpenDrawer) {
          drawerPanel.classList.add("active");
        } else {
          drawerPanel.classList.remove("active");
        }
      }
    }

    _ref(name) {
      return this.querySelector('[data-ref="' + name + '"]');
    }

    _text(refName, value) {
      var el = this._ref(refName);
      if (el) el.textContent = value != null ? value : "";
    }

    _syncSelect(refName, value) {
      var el = this._ref(refName);
      if (el && el.value !== value) el.value = value;
    }

    _toggleError(refName, hasError) {
      var el = this._ref(refName);
      if (el) {
        el.classList.toggle("error", hasError);
      }
    }

    // ── Event binding ────────────────────────────────────────────
    _bindEvents() {
      var self = this;

      // data-action elements
      this.querySelectorAll("[data-action]").forEach(function (el) {
        var parts = el.getAttribute("data-action").split(":");
        var event = parts[0];
        var method = parts[1];
        el.addEventListener(event, function (e) {
          // For drawer overlay, only trigger on self (not child clicks)
          if (
            el.hasAttribute("data-ref") &&
            el.getAttribute("data-ref") === "drawerOverlay" &&
            e.target !== el
          ) {
            return;
          }
          if (typeof self[method] === "function") {
            self[method].call(self, e, el);
          }
        });
      });

      // PRISM direction selects
      this.querySelectorAll("[data-prism-select]").forEach(function (sel) {
        sel.addEventListener("change", function () {
          var path = sel.getAttribute("data-prism-select");
          self._setPrismDirection(path, sel.value);
        });
      });

      // Comment textarea
      var commentEl = this._ref("commentField");
      if (commentEl) {
        commentEl.addEventListener("input", function () {
          self.state.manualFormField.COMMENT = commentEl.value;
        });
      }

      // File input
      var fileInput = this._ref("rxFileInput");
      if (fileInput) {
        fileInput.addEventListener("input", function (e) {
          self.handleInput(e);
        });
      }
    }

    // ── Message listeners ────────────────────────────────────────
    _initMessageListeners() {
      var self = this;

      // PD iframe messages
      this._pdMessageHandler = function (event) {
        if (event.origin !== "https://pd.eazyvisi.com") return;
        var data = event.data;
        if (data.pd) {
          self.state.manualFormField.PUPIL_RL.RL = String(data.pd);
          if (self.state.isOpenPD) {
            self.togglePDOption();
          }
          self._onFieldChange("PUPIL_RL.RL", String(data.pd));
        }
        if (data.photo) {
          self.state.pdImage = data.photo;
        }
        self.closeIframePD();
        self.render();
      };
      window.addEventListener("message", this._pdMessageHandler);

      // Temple iframe messages
      this._templeMessageHandler = function (event) {
        var TEMPLE_URL = self._ctx.TEMPLE_IFRAME_URL;
        if (TEMPLE_URL && event.origin !== TEMPLE_URL) return;

        var data = event.data;
        if (data.uuids && data.uuids.length > 0) {
          self._ctx.temple1 = data.uuids[0];
          self._ctx.temple2 = data.uuids[1];
          if (data.measurementId) {
            self._ctx.measurementId = data.measurementId;
          }
        }
        if (data.close) {
          self.submitManualEntering();
        }
      };
      window.addEventListener("message", this._templeMessageHandler);
    }

    // ── Mask size listener ───────────────────────────────────────
    _initMaskSizeListener() {
      var self = this;
      var maskSizeInput = document.querySelector("[data-mask-size-input]");
      if (maskSizeInput) {
        maskSizeInput.addEventListener("input", function (e) {
          self.state.maskSize = e.target.value;
        });
        if (maskSizeInput.value) {
          this.state.maskSize = maskSizeInput.value;
        }
      }
    }

    // ── Scroll observer ──────────────────────────────────────────
    _initScrollObserver() {
      var self = this;
      var scrollable = this._ref("scrollableContent");
      if (!scrollable) return;

      scrollable.addEventListener("scroll", function () {
        self._updateScrollButtons(scrollable);
      });

      setTimeout(function () {
        self._updateScrollButtons(scrollable);
      }, 200);
    }

    _updateScrollButtons(el) {
      if (!el) return;
      var scrollTop = el.scrollTop;
      var scrollHeight = el.scrollHeight;
      var clientHeight = el.clientHeight;

      if (scrollHeight <= clientHeight) {
        this.state.isShowScrollUpBtn = false;
        this.state.isShowScrollDownBtn = false;
        this.render();
        return;
      }

      var threshold = 50;
      var isAtTop = scrollTop < threshold;
      var isAtBottom = scrollTop + clientHeight > scrollHeight - threshold;

      this.state.isShowScrollUpBtn =
        !isAtTop && this.state.step === "MANUAL";
      this.state.isShowScrollDownBtn =
        !isAtBottom && this.state.step === "MANUAL";
      this.render();
    }

    // ── Drawer ───────────────────────────────────────────────────
    openDrawer(e, el) {
      var key = el.getAttribute("data-drawer-key");
      var eye = el.getAttribute("data-drawer-eye");
      var fieldKey = el.getAttribute("data-field-key");
      var noPlusMinus = el.hasAttribute("data-no-plus-minus");

      this.state.drawerOptionTitle = key;
      this.state.drawerEye = eye;
      this.state.isShowPlusMinusHeading = !noPlusMinus;
      this.state.drawerValues =
        MO.constants.rxValuesByOption[key] || {
          defaultValue: "",
          positiveValues: [],
          negativeValues: [],
        };
      this.state.manualFormFieldKey = fieldKey;
      this.state.isOpenDrawer = true;
      this.render();
      this._renderDrawerValues();

      // Scroll wrapper to top
      var wrapper = this.querySelector(SELECTORS.PARENT_WRAPPER);
      if (wrapper) wrapper.scrollTop = 0;

      // Calculate height
      requestAnimationFrame(
        function () {
          this._calcScrollContainerHeight();
        }.bind(this)
      );
    }

    closeDrawer() {
      this.state.isOpenDrawer = false;
      this.render();
    }

    toggleDrawer() {
      this.state.isOpenDrawer = !this.state.isOpenDrawer;
      this.render();
    }

    // Called by data-action on default value buttons before _renderDrawerValues replaces them
    selectDrawerValue() {
      // No-op — handled by _renderDrawerValues() which replaces buttons with listeners
    }

    handleChangeManualFormField(fieldKey, value) {
      var parts = fieldKey.split(".");
      if (parts[0] === "PRISM") {
        // PRISM.R.vertical or PRISM.L.horizontal
        this.state.manualFormField.PRISM[parts[1]][parts[2]].value = value;
      } else {
        this._setFieldValue(fieldKey, value);
        // Return early; _setFieldValue calls render
        this.closeDrawer();
        return;
      }
      this.closeDrawer();
      this.render();
    }

    _renderDrawerValues() {
      var self = this;
      var dv = this.state.drawerValues;
      if (!dv) return;

      // Both layouts share similar rendering logic
      // SPH/CYL layout refs: drawerNegValsSph, drawerDefaultValSph, drawerPosValsSph
      // Other layout refs: drawerNegVals, drawerDefaultVal, drawerPosVals
      var negRefs = ["drawerNegValsSph", "drawerNegVals"];
      var posRefs = ["drawerPosValsSph", "drawerPosVals"];
      var defRefs = ["drawerDefaultValSph", "drawerDefaultVal"];

      negRefs.forEach(function (refName) {
        var c = self._ref(refName);
        if (!c) return;
        c.innerHTML = "";
        (dv.negativeValues || []).forEach(function (val) {
          var btn = document.createElement("div");
          btn.setAttribute("role", "button");
          btn.className =
            "tw-px-2.5 tw-text-[14px] tw-text-center hover:tw-bg-grey-info/090 tw-py-1";
          btn.textContent = val;
          btn.addEventListener("click", function () {
            self.handleChangeManualFormField(
              self.state.manualFormFieldKey,
              val
            );
          });
          c.appendChild(btn);
        });
      });

      posRefs.forEach(function (refName) {
        var c = self._ref(refName);
        if (!c) return;
        c.innerHTML = "";
        (dv.positiveValues || []).forEach(function (val) {
          var btn = document.createElement("div");
          btn.setAttribute("role", "button");
          btn.className =
            "tw-px-2.5 tw-text-[14px] tw-text-center hover:tw-bg-grey-info/090 tw-py-1";
          btn.textContent = val;
          btn.addEventListener("click", function () {
            self.handleChangeManualFormField(
              self.state.manualFormFieldKey,
              val
            );
          });
          c.appendChild(btn);
        });
      });

      // Default value buttons
      defRefs.forEach(function (refName) {
        var btn = self._ref(refName);
        if (!btn) return;
        btn.textContent = dv.defaultValue || "";
        var newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener("click", function () {
          self.handleChangeManualFormField(
            self.state.manualFormFieldKey,
            dv.defaultValue
          );
        });
      });
    }

    _calcScrollContainerHeight() {
      var parent = this.querySelector(SELECTORS.PARENT);
      if (!parent) return;
      var h1 = this._getHeightBySelector(SELECTORS.DRAWER_HEADING);
      var h2 = this._getHeightBySelector(
        SELECTORS.DRAWER_OPTIONS_VALUES_HEADING
      );
      var h3 = this._getHeightBySelector(SELECTORS.DYNAMIC_VALUES);
      var h4 = this._getHeightBySelector(SELECTORS.DEFAULT_VALUE_BTN);
      parent.style.height = h3 + h1 + h2 + h4 + "px";
    }

    _getHeightBySelector(selector) {
      var el = this.querySelector(selector);
      return el ? el.getBoundingClientRect().height : 0;
    }

    // ── PRISM direction setter ───────────────────────────────────
    _setPrismDirection(path, value) {
      // path: "PRISM.R.vertical.direction" etc.
      var parts = path.split(".");
      this.state.manualFormField[parts[0]][parts[1]][parts[2]][parts[3]] =
        value;
      this.render();
    }

    // ── Steps ────────────────────────────────────────────────────
    setStep(step) {
      this.state.step = step;
      if (step === "MANUAL") {
        var self = this;
        setTimeout(function () {
          var scrollable = self._ref("scrollableContent");
          if (scrollable) self._updateScrollButtons(scrollable);
        }, 200);
      } else {
        this.state.isShowScrollUpBtn = false;
        this.state.isShowScrollDownBtn = false;
      }
      this.render();
    }

    // ── Scanner animation ──────────────────────────────────────
    initScannerAnimation() {
      this.destroyScannerAnimation();

      var self = this;
      var container = this._ref("scannerOverlay");
      if (!container) return;

      var imageWrap = container.querySelector(".rx-scanner__image-wrap");
      var canvas = this._ref("scannerCanvas");
      var asciiEl = this._ref("scannerAscii");
      var lineEl = this._ref("scannerLine");
      var progressFill = this._ref("scannerProgressFill");
      var scannerImg = this._ref("scannerImage");

      if (!imageWrap || !canvas || !asciiEl || !lineEl) return;

      // Show prescription image if available
      if (scannerImg && this.state.previewUrl) {
        scannerImg.src = this.state.previewUrl;
        scannerImg.style.display = "";
      }

      var rect = imageWrap.getBoundingClientRect();
      var w = Math.round(rect.width);
      var h = Math.round(rect.height);

      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext("2d");

      // Prescription-like ASCII text
      var rxTerms = [
        "SPH  -2.50  -1.75", "CYL  -0.75  -1.25", "AXIS  090   175",
        "ADD  +2.00  +2.00", "PD   31.5   32.0", "VA   20/20  20/25",
        "OD  -2.50  -0.75x090", "OS  -1.75  -1.25x175",
        "PRISM  0.50  BI  0.25  BU", "NV  +2.00  +2.00",
        "DIST  -2.50  -1.75", "NEAR  -0.50  +0.25",
        "BVD: 12mm  CT: 1.2mm", "DIA: 70mm  OC: 24mm",
        "MATERIAL: CR-39 1.67", "COATING: AR MULTI",
        "RX_VALID: 24_MONTHS", "PUPIL_DIST: 63.5mm",
        "function analyzePrescription(img) {", "  const data = extractFields(img);",
        "  return { sph, cyl, axis, add };", "}",
        "if (confidence > 0.95) validate();", 'parseOCR(buffer, { lang: "rx" });',
      ];

      var charW = 7.2;
      var lineH = 14;
      var cols = Math.floor(w / charW);
      var rows = Math.floor(h / lineH);
      var asciiText = "";
      var termIdx = 0;
      for (var r = 0; r < rows; r++) {
        var line = rxTerms[termIdx % rxTerms.length];
        termIdx++;
        while (line.length < cols) {
          line += "  " + rxTerms[termIdx % rxTerms.length];
          termIdx++;
        }
        asciiText += line.substring(0, cols) + "\n";
      }
      asciiEl.textContent = asciiText;

      // Gradient cache for particles
      var gradCanvas = document.createElement("canvas");
      gradCanvas.width = 16;
      gradCanvas.height = 16;
      var gCtx = gradCanvas.getContext("2d");
      var grad = gCtx.createRadialGradient(8, 8, 0, 8, 8, 8);
      grad.addColorStop(0, "rgba(255,255,255,1)");
      grad.addColorStop(0.3, "rgba(196,181,253,0.8)");
      grad.addColorStop(0.7, "rgba(139,92,246,0.4)");
      grad.addColorStop(1, "transparent");
      gCtx.fillStyle = grad;
      gCtx.beginPath();
      gCtx.arc(8, 8, 8, 0, Math.PI * 2);
      gCtx.fill();

      // Sweep animation — left to right, 8s, loops
      var sweepDuration = 8000;
      var sweepStart = performance.now();
      this._scannerParticles = [];

      function createParticle(x) {
        return {
          x: x,
          y: Math.random() * h,
          vx: Math.random() * 1.2 + 0.2,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 1.2 + 0.4,
          alpha: Math.random() * 0.4 + 0.6,
          life: 1,
          decay: Math.random() * 0.015 + 0.006,
        };
      }

      function animate(now) {
        var sweepElapsed = now - sweepStart;
        var t = Math.min(sweepElapsed / sweepDuration, 1);
        // easeInOutCubic
        t = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        var scanX = t * w;

        // Update CSS
        lineEl.style.transform = "translateX(" + scanX + "px)";
        asciiEl.style.clipPath = "inset(0 " + (100 - t * 100) + "% 0 0)";

        if (progressFill) {
          progressFill.style.width = (t * 100) + "%";
        }

        // Canvas
        ctx.clearRect(0, 0, w, h);
        ctx.globalCompositeOperation = "lighter";

        // Spawn particles at scan line
        if (t < 1) {
          for (var i = 0; i < 2; i++) {
            self._scannerParticles.push(createParticle(scanX));
          }
        }

        if (self._scannerParticles.length > 300) {
          self._scannerParticles.splice(0, self._scannerParticles.length - 300);
        }

        for (var i = self._scannerParticles.length - 1; i >= 0; i--) {
          var p = self._scannerParticles[i];
          p.x += p.vx;
          p.y += p.vy;
          p.life -= p.decay;

          if (p.life <= 0 || p.x > w + 10) {
            self._scannerParticles.splice(i, 1);
            continue;
          }

          var fadeAlpha = 1;
          var fadeZone = 30;
          if (p.y < fadeZone) fadeAlpha = p.y / fadeZone;
          else if (p.y > h - fadeZone) fadeAlpha = (h - p.y) / fadeZone;
          fadeAlpha = Math.max(0, Math.min(1, fadeAlpha));

          ctx.globalAlpha = p.alpha * p.life * fadeAlpha;
          ctx.drawImage(gradCanvas, p.x - p.radius, p.y - p.radius, p.radius * 2, p.radius * 2);
        }

        // Glow on canvas at scan line
        if (t < 1) {
          ctx.globalCompositeOperation = "lighter";
          var lineGrad = ctx.createLinearGradient(scanX - 8, 0, scanX + 8, 0);
          lineGrad.addColorStop(0, "rgba(139,92,246,0)");
          lineGrad.addColorStop(0.5, "rgba(196,181,253,0.5)");
          lineGrad.addColorStop(1, "rgba(139,92,246,0)");
          ctx.globalAlpha = 1;
          ctx.fillStyle = lineGrad;
          ctx.fillRect(scanX - 8, 0, 16, h);
        }

        // Reset and loop
        if (sweepElapsed >= sweepDuration) {
          sweepStart = now;
          self._scannerParticles = [];
        }

        self._scannerRaf = requestAnimationFrame(animate);
      }

      this._scannerRaf = requestAnimationFrame(animate);
    }

    destroyScannerAnimation() {
      if (this._scannerRaf) {
        cancelAnimationFrame(this._scannerRaf);
        this._scannerRaf = null;
      }
      this._scannerParticles = [];
    }

    // ── File handling ────────────────────────────────────────────
    handleInput(e) {
      var file = e.target.files[0];
      if (!file) return;
      this.state.file = file;
      this.state.previewUrl = URL.createObjectURL(file);
      this.render();
      this.submitFileEntering();
      e.target.value = "";
    }

    removeFile() {
      document.dispatchEvent(
        new CustomEvent("upload:removed", { detail: this.state.data })
      );
      this.state.file = null;
      this.state.uniqueId = null;
      this.state.rxSource = null;
      this.state.data = null;
      this.state.previewUrl = "";
      this.setStep("INITIAL");
    }

    handleClickReturn(e, el) {
      var step = (el && el.getAttribute("data-return-step")) || "INITIAL";
      this.setStep(step);
      this.state.file = null;
      this._resetForm();
    }

    _resetForm() {
      this.state.manualFormField = defaultManualFormField();
      this.state.isOpenPD = false;
      this.state.isOpenPrism = false;
      this.state.requiredFields = [];
      this.render();
    }

    // ── Close popup → dispatch to parent ─────────────────────────
    closePopup() {
      this.handleClickReturn(null, null);
      this.dispatchEvent(
        new CustomEvent("rx-toggle-popup", { bubbles: true })
      );
    }

    closeAndReturn() {
      this.dispatchEvent(
        new CustomEvent("rx-toggle-popup", { bubbles: true })
      );
      var self = this;
      setTimeout(function () {
        self.handleClickReturn(null, null);
      }, 200);
    }

    // ── PD iframe ────────────────────────────────────────────────
    openIframePD() {
      var html =
        '<div id="pd-iframe" class="tw-fixed tw-z-full tw-inset-0 tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-backdrop-blur-6px tw-bg-white/90">' +
        '<div class="tw-relative tw-w-[75vw] tw-h-[75vh] tw-flex tw-overflow-hidden tw-rounded-[32px]">' +
        '<iframe class="!tw-w-full !tw-h-full" ' +
        'src="https://pd.eazyvisi.com/?primaryColor=FFF&textColor=000&lineColor=FFF&borderRadius=3px&photo=true" ' +
        'width="100%" height="100%" frameborder="0" allow="camera"></iframe>' +
        "</div></div>";
      document.documentElement.insertAdjacentHTML("beforeend", html);

      // Close on overlay click
      var overlay = document.getElementById("pd-iframe");
      if (overlay) {
        overlay.addEventListener("click", function (e) {
          if (e.target === overlay) overlay.remove();
        });
      }
    }

    closeIframePD() {
      var el = document.querySelector("#pd-iframe");
      if (el) el.remove();
    }

    // ── PD toggle ────────────────────────────────────────────────
    togglePDOption() {
      this.state.isOpenPD = !this.state.isOpenPD;
      this.state.manualFormField.PUPIL.L = "None";
      this.state.manualFormField.PUPIL.R = "None";
      // Recalculate required fields for PD
      this._onFieldChange("PUPIL_RL.RL", this.state.manualFormField.PUPIL_RL.RL);
      this._onFieldChange("PUPIL.R", this.state.manualFormField.PUPIL.R);
      this._onFieldChange("PUPIL.L", this.state.manualFormField.PUPIL.L);
      this.render();
    }

    // ── Prism toggle ─────────────────────────────────────────────
    togglePrismBlock() {
      this.state.isOpenPrism = !this.state.isOpenPrism;
      this.render();
    }

    // ── Info popup ───────────────────────────────────────────────
    showInfo(e, el) {
      var contentId = el.getAttribute("data-info-id");
      if (window.infoPopup && window.infoPopup[contentId]) {
        MO.popup.show({ contentId: contentId });
      }
    }

    // ── Scroll buttons ───────────────────────────────────────────
    scrollToBottom() {
      var submitBtn = this._ref("submitBtn");
      if (submitBtn) {
        submitBtn.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }

    scrollToTop() {
      var scrollable = this._ref("scrollableContent");
      if (scrollable) {
        scrollable.scrollTo({ top: 0, behavior: "smooth" });
      }
    }

    // ── File upload + OCR analysis ───────────────────────────────
    async submitFileEntering() {
      var self = this;
      var s = this.state;
      var ctx = this._ctx;
      var currentYear = new Date().getFullYear();

      s.isYearExpired = false;
      s.isYearIssued = false;

      // Determine active type
      this._updateActiveTypeOption();

      // Setup loader
      var texts = [
        "We're reading your prescription",
        "Almost done processing",
        "Getting your prescription ready",
      ];
      s.isWaitAnalyze = true;
      s.isUploading = true;
      s.waitingAnalyzeIndex = 0;
      s.waitingAnalyzeText = texts[0];
      this.render();

      // Start scanner animation after overlay is visible
      requestAnimationFrame(function () {
        self.initScannerAnimation();
      });

      this._loadingInterval = setInterval(
        function () {
          if (s.waitingAnalyzeIndex < 2) {
            s.waitingAnalyzeIndex++;
            s.waitingAnalyzeText = texts[s.waitingAnalyzeIndex];
            self.render();
          }
        },
        3000
      );

      var minWaitPromise = new Promise(function (resolve) {
        setTimeout(resolve, 6000);
      });

      try {
        var analyzedData = await MO.proxyApi.analyzePrescription(s.file);
        await minWaitPromise;

        if (!analyzedData || !analyzedData.success) {
          console.error("[RX Form WC] Analysis failed:", analyzedData && analyzedData.error);
          this.setStep("MANUAL");
          return;
        }

        s.uniqueId = analyzedData.uniqueId;
        var analysis = analyzedData.analysis;
        var prescriptionData = analyzedData.prescriptionData;
        s.rxSource = prescriptionData.prescriptionSource ||
          (analysis && analysis.consensus && analysis.consensus.prescriptionSource) ||
          null;

        // Year checks
        if (
          analysis.consensus.yearExpired &&
          currentYear > analysis.consensus.yearExpired
        ) {
          s.isYearExpired = true;
          this.render();
          return;
        }
        if (
          analysis.consensus.yearIssued &&
          currentYear - analysis.consensus.yearIssued > 2
        ) {
          s.isYearIssued = true;
          this.render();
          return;
        }

        // PD
        if (prescriptionData.isMonocularPd) {
          this._setIfValid("PUPIL.R", prescriptionData.pdRight);
          this._setIfValid("PUPIL.L", prescriptionData.pdLeft);
        } else {
          this._setIfValidPD(prescriptionData.pupillaryDistance);
        }

        // SPH, CYL, AXIS
        var map = [
          {
            field: "SPH",
            keys: ["sphRight", "sphLeft"],
            eyes: ["R", "L"],
          },
          {
            field: "CYL",
            keys: ["cylRight", "cylLeft"],
            eyes: ["R", "L"],
          },
          {
            field: "AXIS",
            keys: ["axisRight", "axisLeft"],
            eyes: ["R", "L"],
          },
        ];

        if (
          this._activeTypeOption === "Progressive" ||
          this._activeTypeOption === "Bifocal" ||
          this._activeTypeOption === "Lined Bifocal"
        ) {
          map.push({
            field: "ADD",
            keys: ["addRight", "addLeft"],
            eyes: ["R", "L"],
          });
        }

        var mf = s.manualFormField;
        for (var i = 0; i < map.length; i++) {
          var m = map[i];
          for (var j = 0; j < m.keys.length; j++) {
            var val = prescriptionData[m.keys[j]];
            if (
              val != null &&
              val !== "" &&
              Number.isFinite(Number(val))
            ) {
              mf[m.field][m.eyes[j]] = val;
              this._onFieldChange(
                m.field + "." + m.eyes[j],
                val
              );
            }
          }
        }

        // PRISM from analysis
        if (analysis && analysis.consensus && analysis.consensus.prescriptionDetails) {
          var details = analysis.consensus.prescriptionDetails;
          var convertBase = function (base) {
            var bm = { BO: "O", BI: "I", BU: "U", BD: "D" };
            return bm[base] || base;
          };

          var hasPrR =
            (details.rightEye && details.rightEye.prismVertical) ||
            (details.rightEye && details.rightEye.prismHorizontal);
          var hasPrL =
            (details.leftEye && details.leftEye.prismVertical) ||
            (details.leftEye && details.leftEye.prismHorizontal);

          if (hasPrR || hasPrL) {
            if (details.rightEye && details.rightEye.prismVertical) {
              mf.PRISM.R.vertical.value =
                details.rightEye.prismVertical;
              mf.PRISM.R.vertical.direction = convertBase(
                details.rightEye.baseVertical || "U"
              );
            }
            if (details.rightEye && details.rightEye.prismHorizontal) {
              mf.PRISM.R.horizontal.value =
                details.rightEye.prismHorizontal;
              mf.PRISM.R.horizontal.direction = convertBase(
                details.rightEye.baseHorizontal || "I"
              );
            }
            if (details.leftEye && details.leftEye.prismVertical) {
              mf.PRISM.L.vertical.value =
                details.leftEye.prismVertical;
              mf.PRISM.L.vertical.direction = convertBase(
                details.leftEye.baseVertical || "U"
              );
            }
            if (details.leftEye && details.leftEye.prismHorizontal) {
              mf.PRISM.L.horizontal.value =
                details.leftEye.prismHorizontal;
              mf.PRISM.L.horizontal.direction = convertBase(
                details.leftEye.baseHorizontal || "I"
              );
            }
            s.isOpenPrism = true;
          }
        }

        s.isOpenPD = !!prescriptionData.isMonocularPd;
        this.setStep("MANUAL");
      } finally {
        this.destroyScannerAnimation();
        clearInterval(this._loadingInterval);
        this._loadingInterval = null;
        s.isWaitAnalyze = false;
        s.isUploading = false;
        this.render();
      }
    }

    _setIfValid(fieldPath, value) {
      if (
        value != null &&
        value !== "" &&
        Number.isFinite(Number(value))
      ) {
        this._setFieldValue(fieldPath, String(value));
      }
    }

    _setIfValidPD(value) {
      if (
        value != null &&
        value !== "" &&
        Number.isFinite(Number(value))
      ) {
        this.state.manualFormField.PUPIL_RL.RL = String(value);
        this._onFieldChange("PUPIL_RL.RL", String(value));
      }
    }

    // ── Submit manual entry ──────────────────────────────────────
    async submitManualEntering() {
      var s = this.state;
      var ctx = this._ctx;
      var mf = s.manualFormField;

      // Validate required fields
      if (s.requiredFields.length > 0) {
        console.warn("[RX Form WC] Required fields missing:", s.requiredFields);
        this.render();
        return;
      }

      var pdValues = this._createPDValue();
      var prescriptionPayload = this._getPrescriptionPayload(pdValues);

      try {
        s.isUploading = true;
        this.render();

        var prescriptionValues = await MO.proxyApi.savePrescription({
          orderId: "prescription-" + Date.now(),
          lineItemId: "prescription-" + Date.now(),
          prescriptionData: prescriptionPayload,
        });

        var hash = Date.now();
        var _bundleHash =
          "bundle-hash-" +
          hash +
          "-" +
          ctx.rxLensSelectedVariant.id +
          "-" +
          ctx.selectedVariant.id;
        var props = MO.helpers.buildProperties(ctx);

        var rxLensProduct = {
          id: ctx.rxLensSelectedVariant.id,
          quantity: 1,
          properties: Object.assign(
            {
              _prescription_type: "manual",
              rxOrder: "true",
              "Frame Description":
                ctx.frameTag + " - " + ctx.selectedVariant.title,
              Coating:
                ctx.isAddedCoatingProduct && ctx.lensCoatingSelectedVariant
                  ? ctx.lensCoatingSelectedVariant.sku
                  : "COT",
              _bundleHash: _bundleHash,
              "_pd-image": s.pdImage,
              temple1: ctx.temple1,
              temple2: ctx.temple2,
              _frame_variant_id: ctx.selectedVariant.id,
            },
            props
          ),
        };

        if (s.uniqueId) {
          rxLensProduct.properties["uniqueId"] = s.uniqueId;
        }
        if (s.rxSource) {
          rxLensProduct.properties["rxsource"] = s.rxSource;
        }
        if (ctx.measurementId) {
          rxLensProduct.properties["measurementId"] = ctx.measurementId;
        }
        if (s.maskSize) {
          rxLensProduct.properties["Mask Size"] = s.maskSize;
        }

        // PD properties
        if (!s.isOpenPD) {
          rxLensProduct.properties["Pupillary Distance"] =
            parseFloat(pdValues.left) + parseFloat(pdValues.right) + "mm";
        } else {
          rxLensProduct.properties["Pupillary Distance Left"] =
            parseFloat(pdValues.left) + "mm";
          rxLensProduct.properties["Pupillary Distance Right"] =
            parseFloat(pdValues.right) + "mm";
        }

        if (mf.COMMENT) {
          rxLensProduct.properties["Buyer's notes"] = mf.COMMENT;
        }

        if (s.isOpenPrism) {
          rxLensProduct.properties["isPrism"] = "true";
          rxLensProduct.properties["Service"] = "FOCM";
        }

        // Segment height based on type
        var activeType = this._activeTypeOption;
        if (activeType === "Bifocal" || activeType === "Lined Bifocal") {
          rxLensProduct.properties["Segment Height"] = "-2";
        }
        if (activeType === "Progressive") {
          rxLensProduct.properties["Segment Height"] =
            ctx.activeEyewear === "lens_product" ? "2.5" : "4";
        }

        // RX UID from save response
        if (
          prescriptionValues &&
          prescriptionValues.success &&
          prescriptionValues.data &&
          prescriptionValues.data.rxUID
        ) {
          rxLensProduct.properties["Prescription RX UID"] =
            prescriptionValues.data.rxUID;
        }

        var frameProduct = {
          id: ctx.selectedVariant.id,
          quantity: 1,
          properties: Object.assign({ _bundleHash: _bundleHash }, props),
        };

        if (
          ctx.selectedVariant.title.toLowerCase() === "custom size" &&
          ctx.customSize
        ) {
          frameProduct.properties["Custom Size"] = ctx.customSize;
        }

        var items = [rxLensProduct, frameProduct];

        if (ctx.isAddedCoatingProduct && ctx.lensCoatingSelectedVariant) {
          items.push({
            id: ctx.lensCoatingSelectedVariant.id,
            quantity: 1,
            properties: Object.assign({ _bundleHash: _bundleHash }, props),
          });
        }

        await MO.api.addItems(items);
        window.location.href = "/cart";
      } catch (e) {
        s.isUploading = false;
        s.uploaded = false;
        console.error("[RX Form WC] Submit error:", e);
        this.render();
      }
    }

    // ── Go to checkout (send prescription later) ─────────────────
    async goToCheckout() {
      var s = this.state;
      var ctx = this._ctx;

      try {
        s.isUploading = true;
        this.render();

        var hash = Date.now();
        var _bundleHash =
          "bundle-hash-" +
          hash +
          "-" +
          ctx.rxLensSelectedVariant.id +
          "-" +
          ctx.selectedVariant.id;
        var props = MO.helpers.buildProperties(ctx);

        var rxLensProduct = {
          id: ctx.rxLensSelectedVariant.id,
          quantity: 1,
          properties: Object.assign(
            {
              _bundleHash: _bundleHash,
              "Frame Description":
                ctx.frameTag + " - " + ctx.selectedVariant.title,
              rxOrder: "true",
              Coating:
                ctx.isAddedCoatingProduct && ctx.lensCoatingSelectedVariant
                  ? ctx.lensCoatingSelectedVariant.sku
                  : "COT",
              "_pd-image": s.pdImage,
              temple1: ctx.temple1,
              temple2: ctx.temple2,
              _frame_variant_id: ctx.selectedVariant.id,
            },
            props
          ),
        };

        if (ctx.measurementId) {
          rxLensProduct.properties["measurementId"] = ctx.measurementId;
        }
        if (s.maskSize) {
          rxLensProduct.properties["Mask Size"] = s.maskSize;
        }

        // Segment height based on type
        var activeType = this._activeTypeOption;
        if (activeType === "Bifocal" || activeType === "Lined Bifocal") {
          rxLensProduct.properties["Segment Height"] = "-2";
        }
        if (activeType === "Progressive") {
          rxLensProduct.properties["Segment Height"] =
            ctx.activeEyewear === "lens_product" ? "2.5" : "4";
        }

        var frameProduct = {
          id: ctx.selectedVariant.id,
          quantity: 1,
          properties: Object.assign({ _bundleHash: _bundleHash }, props),
        };

        if (
          ctx.selectedVariant.title.toLowerCase() === "custom size" &&
          ctx.customSize
        ) {
          frameProduct.properties["Custom Size"] = ctx.customSize;
        }

        var items = [rxLensProduct, frameProduct];

        if (ctx.isAddedCoatingProduct && ctx.lensCoatingSelectedVariant) {
          items.push({
            id: ctx.lensCoatingSelectedVariant.id,
            quantity: 1,
            properties: Object.assign({ _bundleHash: _bundleHash }, props),
          });
        }

        await MO.api.addItems(items);
        window.location.href = "/cart";
      } catch (e) {
        s.isUploading = false;
        s.uploaded = false;
        console.error("[RX Form WC] Checkout error:", e);
        this.render();
      }
    }

    // ── PD value computation ─────────────────────────────────────
    _createPDValue() {
      var mf = this.state.manualFormField;
      var value = 32.0;

      if (!this.state.isOpenPD) {
        value =
          mf.PUPIL_RL.RL === "None"
            ? 32.0
            : parseFloat(mf.PUPIL_RL.RL) / 2;
        return {
          right: value.toFixed(1),
          left: value.toFixed(1),
        };
      }
      return {
        right:
          mf.PUPIL.R === "None" ? value.toFixed(1) : mf.PUPIL.R,
        left:
          mf.PUPIL.L === "None" ? value.toFixed(1) : mf.PUPIL.L,
      };
    }

    // ── Build prescription payload ───────────────────────────────
    _getPrescriptionPayload(pdValues) {
      var mf = this.state.manualFormField;
      var ctx = this._ctx;

      var payload = {
        data: {
          prescriptionData: {
            sphRight: mf.SPH.R,
            cylRight: mf.CYL.R,
            axisRight: mf.AXIS.R,
            addRight: mf.ADD.R === "N/A" ? "0.00" : mf.ADD.R,
            sphLeft: mf.SPH.L,
            cylLeft: mf.CYL.L,
            axisLeft: mf.AXIS.L,
            addLeft: mf.ADD.L === "N/A" ? "0.00" : mf.ADD.L,
          },
        },
      };

      // Prism
      if (this.state.isOpenPrism) {
        var pd = payload.data.prescriptionData;
        pd.isPrism = true;
        pd.Service = "FOCM";
        pd.prismFormat = "directional";
        pd.prismRight = {
          vertical: {
            value: mf.PRISM.R.vertical.value,
            base: MO.rxHelpers.convertBaseDirection(
              mf.PRISM.R.vertical.direction
            ),
          },
          horizontal: {
            value: mf.PRISM.R.horizontal.value,
            base: MO.rxHelpers.convertBaseDirection(
              mf.PRISM.R.horizontal.direction
            ),
          },
        };
        pd.prismLeft = {
          vertical: {
            value: mf.PRISM.L.vertical.value,
            base: MO.rxHelpers.convertBaseDirection(
              mf.PRISM.L.vertical.direction
            ),
          },
          horizontal: {
            value: mf.PRISM.L.horizontal.value,
            base: MO.rxHelpers.convertBaseDirection(
              mf.PRISM.L.horizontal.direction
            ),
          },
        };
      }

      // Segment height
      var activeType = this._activeTypeOption;
      if (activeType === "Single Vision") {
        payload.data.prescriptionData.segmentHeight = null;
      }
      if (activeType === "Bifocal" || activeType === "Lined Bifocal") {
        payload.data.prescriptionData.segmentHeight = "-2";
      }
      if (activeType === "Progressive") {
        payload.data.prescriptionData.segmentHeight = "4";
      }

      // PD
      payload.data.prescriptionData.isMonocularPd = this.state.isOpenPD;
      if (this.state.isOpenPD) {
        payload.data.prescriptionData.pdLeft =
          parseFloat(pdValues.left) + "mm";
        payload.data.prescriptionData.pdRight =
          parseFloat(pdValues.right) + "mm";
      } else {
        payload.data.prescriptionData.pdValue =
          parseFloat(pdValues.left) + parseFloat(pdValues.right) + "mm";
      }

      return payload;
    }

    // ── Wheel handler (prevent page scroll when popup is open) ───
    handleWheel(e) {
      var scrollable = this._ref("scrollableContent");
      if (!scrollable) return;
      var delta = e.deltaY;
      var currentScroll = scrollable.scrollTop;
      var maxScroll = scrollable.scrollHeight - scrollable.clientHeight;
      if (
        (delta > 0 && currentScroll < maxScroll) ||
        (delta < 0 && currentScroll > 0)
      ) {
        scrollable.scrollTop += delta;
        var self = this;
        requestAnimationFrame(function () {
          self._updateScrollButtons(scrollable);
        });
      }
    }
  }

  customElements.define("rx-form-safety", RxFormSafety);
})();
